import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Gift, Users, Crown, Building2, Check, Copy } from "lucide-react";
import PageHero from "../components/PageHero";
import { useMedia } from "../hooks/useMedia";
import { toast } from "sonner";

const TIERS = [
  {
    Icon: Gift,
    name: "Neighbor",
    who: "One-off referral",
    reward: "$150 service credit",
    detail: "Refer any commercial property that books a single one-off service. You receive $150 credit toward your next Bespoke visit.",
  },
  {
    Icon: Users,
    name: "Portfolio",
    who: "Any signed retainer",
    reward: "$500 credit + free month",
    detail: "Refer a property that signs any monthly, quarterly, or annual retainer. Receive $500 credit plus one month of your own retainer service free.",
  },
  {
    Icon: Crown,
    name: "Concierge",
    who: "Property manager (5+ properties)",
    reward: "$2,500 credit + white-glove onboarding",
    detail: "Introduce a portfolio manager who signs across five or more properties. Receive $2,500 in service credit and a dedicated concierge assigned to your account.",
  },
  {
    Icon: Building2,
    name: "Enterprise",
    who: "Regional / national account",
    reward: "Custom split · negotiated per deal",
    detail: "Broker an introduction that closes into a regional or national account (25+ properties). We negotiate a custom referral split with you, structured as service credit, direct payment, or both.",
  },
];

const STEPS = [
  { n: "01", title: "Get your link", body: "Every retainer client gets a personal referral code. Copy it below or ask your account lead." },
  { n: "02", title: "Send the intro", body: "Send your code to the property manager, restaurateur, or building owner — one email or one text is enough." },
  { n: "03", title: "They book a walk-through", body: "We walk their property, deliver the proposal, and mention your code on the intake form. That's it." },
  { n: "04", title: "You get paid", body: "The moment their first invoice clears, your reward posts to your account. Redeem as service credit or roll it forward." },
];

export default function Referral() {
  const { url } = useMedia();
  const [code] = useState("BSP-YOUR-CODE"); // placeholder — production would pull from auth
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Referral code copied");
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-screen" data-testid="referral-page">
      <PageHero
        eyebrow="Referral Program"
        title={<>Pay it <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>forward.</span></>}
        subtitle="Four out of five new Bespoke clients come from an existing client. This is how we say thank you."
        image={url("referral-hero") || url("outdoor-lighting")}
        kicker="No forms. No expiration. No cap on how much you can earn."
      />

      {/* Tiers */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">Reward Tiers</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              Four ways to <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>get paid.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {TIERS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: i * 0.08 }}
                className="glass-card p-10 flex flex-col"
                data-testid={`tier-${t.name.toLowerCase()}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-14 h-14 flex items-center justify-center" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", background: "rgba(198,168,124,0.06)" }}>
                    <t.Icon size={18} strokeWidth={1.4} style={{ color: "var(--gold)" }} />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--gold-2)" }}>{t.reward}</div>
                </div>
                <h3 className="mt-8 font-display text-3xl" style={{ letterSpacing: "-0.025em" }}>{t.name}</h3>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>{t.who}</div>
                <p className="mt-6 font-body" style={{ color: "var(--text-2)", lineHeight: 1.7, fontSize: 15 }}>{t.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-28 md:py-36" style={{ background: "var(--bg-2)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-3 overline pt-2">How It Works</div>
            <h2 className="md:col-span-9 font-display text-4xl md:text-6xl" style={{ letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              Four steps. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Zero forms.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 md:gap-8">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="glass-card p-8"
                data-testid={`ref-step-${s.n}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>{s.n}</div>
                <h3 className="mt-5 font-display text-xl" style={{ letterSpacing: "-0.02em" }}>{s.title}</h3>
                <p className="mt-4 font-body" style={{ color: "var(--text-2)", lineHeight: 1.7, fontSize: 15 }}>{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Copy your code */}
      <section className="py-28 md:py-32" style={{ background: "var(--bg)" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="overline mb-6 justify-center">Your Personal Code</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[0.95]" style={{ letterSpacing: "-0.03em" }}>
            Ready to <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>send it?</span>
          </h2>
          <div className="mt-10 glass-card p-6 md:p-8 flex items-center justify-between gap-4">
            <div className="font-mono text-xl md:text-3xl tracking-widest" style={{ color: "var(--gold-2)" }} data-testid="referral-code">{code}</div>
            <button
              onClick={copy}
              className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em] px-5 py-3 transition-all"
              style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", color: "var(--gold)" }}
              data-testid="copy-referral-code"
              data-cursor="hover"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
            Not yet a client? <Link to="/pricing" className="underline underline-offset-4" style={{ color: "var(--gold)" }}>See instant plans</Link> — every retainer gets a personal code.
          </p>
        </div>
      </section>

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg-2)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          Send one intro. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Get paid for years.</span>
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/quote" className="btn-glow" data-testid="referral-cta-quote" data-cursor="hover">
            Introduce a Property <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
